/* =========================================================================
   assets/soho-shop-modal.js  —  SOHO Bohemian
   "Shop the video": the ONE place that knows how to sell out of a reel.

   Owner of three layers at once, on purpose: the markup it generates, the
   CSS that styles it (assets/soho-shop-modal.css) and the behaviour. The
   27/08 round shipped CSS waiting on classes the JS never created; a single
   owner for the three layers is the structural fix, and the class inventory
   at the bottom of this header is the diff list a reviewer can check.

   IMPLEMENTS docs/CONTRATO-SHOP-VIDEO.md — that file is the law. Section
   numbers below refer to it.

   ── WHAT DYLAN ASKED FOR ────────────────────────────────────────────────
   "the girls have an outfit full of many items and i want them to be able to
   shop one or multiple items that they can see in the video" + "i need to be
   able to add collections if the video highlights an entire collection".
   So: tap the bag on a reel -> the video opens BIG with a horizontal strip of
   product cards under it (photo, title, price, arrow). Tap a card -> size
   chips + Add to cart, without ever leaving the video. A collection payload
   fills the same strip from /collections/{h}/products.json.

   ── HARD RULES THIS FILE OBEYS ──────────────────────────────────────────
   (7)  It never touches a <video> it was not handed. There is no
        `document.querySelectorAll('video')` here and there may never be: that
        call would pause the "On the girls" rail on the nine live set pages
        and the home hero. The only videos this file pauses are the ones
        inside the cards the CALLER passed in `opts.card` / `opts.group`, and
        each one is put back exactly as it was found on close.
   (1)  Nothing here decides whether page content exists. If this script never
        loads, the bag stays an <a href> to the PDP and the store still sells.
        We publish window.SohoShopVideo only when we can actually do the job
        (fetch + Promise present), so a caller's `if (window.SohoShopVideo)`
        is an honest test.
   (11) No rAF-throttled scroll listener. The rail's dot state is a plain
        passive scroll listener doing two number comparisons. The open
        transition is driven by a class added SYNCHRONOUSLY after a forced
        reflow — never inside requestAnimationFrame, which does not fire in a
        backgrounded tab and would leave a transparent full-screen overlay
        eating every click on the page. Belt and braces for the same failure:
        the root is `pointer-events:none` until `.is-open` lands.
   (aos) No data-aos, no opacity-0 default that depends on a class arriving.

   ── Z-INDEX (§4) ────────────────────────────────────────────────────────
   Theme overlays: predictive-search 4000, dialog/.drawer__underlay 6000,
   .drawer__inner 6001, .collection__sidebar__slider 6100, .skip-link 15000.
   SOHO widgets: .ss-float 2147482999, .soho-fg / popup 2147483000.
   .soho-shopv is an overlay the shopper OPENED -> 2147482500, above the cart
   drawer, below the floating widgets. Nothing here is persistent.

   ── SCROLL LOCK (§4) ────────────────────────────────────────────────────
   The theme signals its own locks with `html[data-scroll-locked]`. We read it
   AND write it, in the one direction that cannot fight: if the attribute is
   already there the theme owns the lock and we touch neither it nor
   documentElement.style.overflow; if it is absent we set both and remember
   that they are ours, so close() restores exactly what was there before.
   Writing it is what makes the persistent widgets (the story dock rail hides
   itself on `html[data-scroll-locked]`) get out of the way of our dialog.

   ── PRICES: THE TRAP THIS FILE IS BUILT AROUND ──────────────────────────
   Two Shopify endpoints, two different units for the same field name:
     GET /products/{h}.js                  ->  price: 5800        (INTEGER cents)
     GET /collections/{h}/products.json    ->  price: "58.00"     (STRING dollars)
   So `centsOf()` is the only place that reads a price: a number is cents, a
   string is dollars and gets multiplied by 100. Getting this wrong shows a
   $58 bikini at $0.58 or at $580,000. Liquid always sends cents (§1 rule 1).

   ── CLASS INVENTORY (everything this file writes; the CSS styles this list) ─
   Contract §2:  .soho-shopv .soho-shopv__scrim .soho-shopv__dialog
                 .soho-shopv__close .soho-shopv__stage .soho-shopv__video
                 .soho-shopv__nav (--prev/--next) .soho-shopv__title
                 .soho-shopv__tray .soho-shopv__rail .soho-shopv__card
                 .soho-shopv__card__img .soho-shopv__card__body
                 .soho-shopv__card__title .soho-shopv__card__price
                 .soho-shopv__card__was .soho-shopv__card__go
                 .soho-shopv__dots .soho-shopv__dot .soho-shopv__picker
                 .soho-shopv__opt .soho-shopv__add .soho-shopv__all
                 .soho-shopv__err
   States:       .soho-shopv.is-open  .soho-shopv.is-closing
                 .soho-shopv.is-loading  .soho-shopv__card.is-active
                 .soho-shopv__card.is-sold-out  .soho-shopv__opt.is-selected
                 .soho-shopv__opt.is-unavailable  .soho-shopv__add.is-busy
                 .soho-shopv__add.is-done  .soho-shopv__dot.is-active
   Ours, same namespace, declared here so the CSS is never orphaned:
                 .soho-shopv__tray--compact  .soho-shopv__card__flag
                 .soho-shopv__foot  .soho-shopv__wait  .soho-shopv__opts
                 .soho-shopv__all__go
                 .soho-shopv__picker__head .soho-shopv__picker__img
                 .soho-shopv__picker__info .soho-shopv__picker__title
                 .soho-shopv__picker__price .soho-shopv__picker__was
                 .soho-shopv__picker__close .soho-shopv__picker__group
                 .soho-shopv__picker__label .soho-shopv__picker__pdp
   ========================================================================= */
(function () {
  'use strict';

  /* §8 — both sections load this asset, so on a page carrying the reels row
     AND the story dock the tag is in the DOM twice and both copies run. One
     modal, one API object, one product cache per page. */
  if (window.SohoShopVideo) return;

  /* Without fetch/Promise we cannot POST to /cart/add.js, so we publish
     NOTHING: callers test `window.SohoShopVideo` and, finding it absent,
     leave the bag <a href> to navigate to the PDP. A half-working modal that
     cannot add to cart is worse than no modal at all. */
  if (!window.fetch || !window.Promise || !window.Map || !window.WeakMap) return;

  /* ------------------------------------------------------------ constants */

  /* §1 — the handle regex, written once, checked on the way in AND on the way
     out. The public endpoint that feeds the dashboard bridge is not
     authenticated; "the dashboard would never write that" is hope, not a
     guarantee. */
  /* The `_` belongs in this class. Without it, `untitled-apr29_14-55` — "The
     Ela Top", $139, 1 of this store's 81 products — was dropped from the strip
     with nothing anywhere to say why. The class is here to bar `/`, `.`, `:`
     and `%`, and `_` is none of those. */
  var HANDLE_RE = /^[a-z0-9][a-z0-9_-]{0,254}$/;
  var MAX_PRODUCTS = 12; /* §1 rule 3 — same ceiling as Liquid/editor/reader */
  var MAX_PARALLEL = 6; /* §5 — at most six /products/{h}.js in flight */
  var COLLECTION_LIMIT = 24; /* §5 — /collections/{h}/products.json?limit=24 */
  var FETCH_TIMEOUT = 6000;
  var CLOSE_MS = 220; /* must match the transition in soho-shop-modal.css */
  var DONE_MS = 1600; /* how long ".is-done" sits on the tray-mode button */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* §5 — one cache per page, keyed by handle, never refetched. */
  var productCache = new Map();
  /* Parsing the same <script> on every prev/next tap is pure waste. */
  var payloadCache = new WeakMap();
  /* Everything a mounted tray needs, keyed by the tray element itself, so a
     tray in the story dock and the tray in the modal share one code path. */
  var trayState = new WeakMap();

  /* ----------------------------------------------------------- tiny utils */

  function isEl(node) {
    return !!node && node.nodeType === 1;
  }

  function str(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null && text !== '') node.textContent = text;
    return node;
  }

  /* innerHTML is used for icons and for icons ONLY: the argument is always a
     literal defined a few lines below, never anything that came off the wire.
     Every piece of merchant/shopper data goes in through textContent. */
  function icon(markup, className) {
    var span = document.createElement('span');
    span.className = className || '';
    span.setAttribute('aria-hidden', 'true');
    span.innerHTML = markup;
    return span;
  }

  var SVG_CHEVRON_RIGHT =
    '<svg viewBox="0 0 24 24" focusable="false" width="24" height="24"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var SVG_CHEVRON_LEFT =
    '<svg viewBox="0 0 24 24" focusable="false" width="24" height="24"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var SVG_CLOSE =
    '<svg viewBox="0 0 24 24" focusable="false" width="24" height="24"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

  function reducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  function emit(target, name, detail) {
    try {
      target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || null }));
    } catch (e) {
      /* A browser without the CustomEvent constructor cannot reach this file
         anyway (it has no fetch). Never let a dead event kill an add. */
    }
  }

  /* Locale-aware root. On this store Shopify.routes.root is "/", so every URL
     below is exactly the "/products/{h}" the contract specifies; on a
     localised storefront it becomes "/en-ca/products/{h}", which is the same
     rule ("u is DERIVED from h"), not a different one. */
  function routeRoot() {
    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      (window.theme && window.theme.routes && window.theme.routes.root) ||
      '/';
    if (typeof root !== 'string' || !root) root = '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root;
  }

  function productUrl(handle) {
    return routeRoot() + 'products/' + handle;
  }

  function collectionUrl(handle) {
    return routeRoot() + 'collections/' + handle;
  }

  /* §3 — measured on the live theme: window.theme.routes.cart_add_url is
     "/cart/add". The .js suffix is what makes it the AJAX endpoint. */
  function cartAddUrl() {
    var url =
      (window.theme && window.theme.routes && window.theme.routes.cart_add_url) ||
      routeRoot() + 'cart/add';
    if (typeof url !== 'string' || !url) url = routeRoot() + 'cart/add';
    return /\.js($|\?)/.test(url) ? url : url + '.js';
  }

  /* §1 rule 1 — the theme formats money, never this file, so a currency
     change in Shopify admin lands here for free. The fallbacks exist because
     a JS error inside theme.js must not blank out every price on the strip. */
  function money(cents) {
    if (cents == null || isNaN(cents)) return '';
    try {
      if (window.theme && typeof window.theme.formatMoney === 'function') {
        var out = window.theme.formatMoney(cents, window.theme.moneyFormat);
        if (out) return out;
      }
    } catch (e) {
      /* fall through */
    }
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        var alt = window.Shopify.formatMoney(cents);
        if (alt) return alt;
      }
    } catch (e2) {
      /* fall through */
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function themeString(key, fallback) {
    try {
      var bag = window.theme && window.theme.strings;
      if (bag && typeof bag[key] === 'string' && bag[key]) return bag[key];
    } catch (e) {
      /* noop */
    }
    return fallback;
  }

  /* THE price reader. See the header: a number is already cents (the .js
     endpoint and Liquid), a string is dollars (the .json endpoint). Anything
     else is refused rather than guessed — a missing price paints nothing and
     the card still sells; a wrong price is a chargeback. */
  function centsOf(value) {
    if (typeof value === 'number') {
      if (!isFinite(value) || value < 0) return null;
      return Math.round(value);
    }
    if (typeof value === 'string') {
      var trimmed = value.trim();
      if (!trimmed) return null;
      if (/^\d+$/.test(trimmed)) return parseInt(trimmed, 10); /* "5800" — Liquid cents as a string */
      if (!/^\d{1,9}([.,]\d{1,2})?$/.test(trimmed)) return null; /* "$58.00" and friends: refused */
      var n = parseFloat(trimmed.replace(',', '.'));
      if (isNaN(n) || n < 0) return null;
      return Math.round(n * 100);
    }
    return null;
  }

  /* Images arrive protocol-relative ("//cdn.shopify.com/..."), absolute https
     or root-relative. Anything else — javascript:, data:, a bare word — is
     dropped and the card falls back to its CSS placeholder. */
  function safeImage(value) {
    var url = str(value);
    if (!url || url.length > 2048) return '';
    if (/^https?:\/\//i.test(url)) return url;
    if (url.indexOf('//') === 0) return url;
    if (url.charAt(0) === '/') return url;
    return '';
  }

  /* Shopify CDN images accept ?width=. Asking for 160px instead of the
     original 1200px file is the difference between a strip that paints with
     the modal and one that paints a second later. */
  function sizedImage(url, width) {
    if (!url) return '';
    if (!/(cdn\.shopify\.com|\/cdn\/shop\/|shopifycdn\.com)/i.test(url)) return url;
    if (/[?&]width=/.test(url)) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  /* ------------------------------------------------------- §1 the payload */

  /* The <script type="application/json" data-soho-shop> is a DIRECT child of
     the card (§1). We look there first; the descendant fallback exists only
     because the dashboard bridge rewrites this node, and it refuses to cross
     into a nested card so a wall of reels can never read its neighbour's. */
  function payloadScript(cardEl) {
    var kids = cardEl.children;
    var i;
    for (i = 0; i < kids.length; i++) {
      if (kids[i].tagName === 'SCRIPT' && kids[i].hasAttribute('data-soho-shop')) return kids[i];
    }
    var found = cardEl.querySelectorAll('script[type="application/json"][data-soho-shop]');
    for (i = 0; i < found.length; i++) {
      var owner = found[i].parentNode;
      while (owner && owner !== cardEl) {
        if (owner.hasAttribute && owner.hasAttribute('data-soho-reel')) break;
        owner = owner.parentNode;
      }
      if (owner === cardEl) return found[i];
    }
    return null;
  }

  function normalizeItem(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var handle = typeof raw.h === 'string' ? raw.h : '';
    /* Not lowercased on purpose: a handle is lowercase by definition, so an
       uppercase one is a sign the payload was built by hand and is not to be
       repaired silently. */
    if (!HANDLE_RE.test(handle)) return null;
    return {
      h: handle,
      t: str(raw.t) || handle,
      u: productUrl(handle) /* §1 rule 2 — derived, never copied */,
      i: safeImage(raw.i),
      pr: centsOf(raw.pr),
      cp: centsOf(raw.cp),
      a: raw.a !== false /* absent means available */
    };
  }

  function normalizeCollection(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var handle = typeof raw.h === 'string' ? raw.h : '';
    if (!HANDLE_RE.test(handle)) return null;
    var count = typeof raw.n === 'number' && isFinite(raw.n) && raw.n > 0 ? Math.round(raw.n) : 0;
    return { h: handle, t: str(raw.t) || handle, u: collectionUrl(handle), n: count };
  }

  /* The object half of the reader. Both public entry points go through it:
     readPayload() after JSON.parse, and mountTray() when a caller hands us an
     object it built itself (the story dock). It is idempotent on purpose —
     running an already-normalised payload through it a second time re-tests
     every handle and re-derives every URL, which is exactly what §1 rule 2
     asks of whoever READS ("quem lê valida DE NOVO"). */
  function normalizePayload(raw) {
    if (!raw || typeof raw !== 'object') return null;

    var out = { t: str(raw.t), p: [], c: normalizeCollection(raw.c) };
    /* Object.create(null), not {}: "constructor", "tostring" and "valueof"
       are all legal Shopify handles, and against a plain object every one of
       them tests truthy through the prototype and silently drops a product
       Dylan tagged. Same reason for state.seen and the option-value set. */
    var seen = Object.create(null);
    if (Object.prototype.toString.call(raw.p) === '[object Array]') {
      for (var i = 0; i < raw.p.length; i++) {
        if (out.p.length >= MAX_PRODUCTS) break;
        var item = normalizeItem(raw.p[i]);
        if (!item) continue;
        if (seen[item.h]) continue; /* the same product twice is a mistake, not a feature */
        seen[item.h] = true;
        out.p.push(item);
      }
    }
    /* §1 rule 4 — nothing to sell means NO modal at all. */
    if (!out.p.length && !out.c) return null;
    return out;
  }

  function parsePayload(text) {
    var raw;
    try {
      raw = JSON.parse(text);
    } catch (e) {
      return null; /* malformed JSON: the card keeps its <a href>. §1 rule 4 */
    }
    return normalizePayload(raw);
  }

  function readPayload(cardEl) {
    if (!isEl(cardEl)) return null;
    if (payloadCache.has(cardEl)) return payloadCache.get(cardEl);
    var node = payloadScript(cardEl);
    var parsed = node ? parsePayload(node.textContent) : null;
    payloadCache.set(cardEl, parsed);
    return parsed;
  }

  /* -------------------------------------------------- §5 the fetch layer */

  /* A six-slot queue. Twelve products on a phone is twelve parallel requests
     on a connection that has to load the video too; six is the ceiling the
     contract sets and this is where it is enforced, once, for every caller. */
  var inflight = 0;
  var waiting = [];

  function pump() {
    while (inflight < MAX_PARALLEL && waiting.length) {
      var job = waiting.shift();
      inflight++;
      var promise;
      try {
        promise = job.run();
      } catch (err) {
        promise = Promise.reject(err);
      }
      promise.then(job.resolve, job.reject);
      promise.then(release, release);
    }
  }

  function release() {
    inflight--;
    pump();
  }

  function limited(run) {
    return new Promise(function (resolve, reject) {
      waiting.push({ run: run, resolve: resolve, reject: reject });
      pump();
    });
  }

  function fetchJSON(url) {
    return new Promise(function (resolve, reject) {
      var settled = false;
      var controller = null;
      try {
        if (window.AbortController) controller = new AbortController();
      } catch (e) {
        controller = null;
      }
      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        if (controller) {
          try {
            controller.abort();
          } catch (e2) {
            /* noop */
          }
        }
        reject(new Error('timeout'));
      }, FETCH_TIMEOUT);

      var options = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
      if (controller) options.signal = controller.signal;

      fetch(url, options)
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
        .then(
          function (data) {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            resolve(data);
          },
          function (error) {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            reject(error);
          }
        );
    });
  }

  /* One shape out of two endpoints. See the header for the price units; the
     other two differences are just as real:
       /products/{h}.js        options: ["Size"]        images: ["//cdn/.."]
       /collections/../.json   options: [{name:"Size"}] images: [{src:"//"}]  */
  function normalizeProduct(raw, fallbackHandle) {
    if (!raw || typeof raw !== 'object') return null;
    var handle = typeof raw.handle === 'string' && HANDLE_RE.test(raw.handle) ? raw.handle : fallbackHandle;
    if (!handle || !HANDLE_RE.test(handle)) return null;

    var optionNames = [];
    var rawOptions = raw.options;
    if (Object.prototype.toString.call(rawOptions) === '[object Array]') {
      for (var o = 0; o < rawOptions.length; o++) {
        var opt = rawOptions[o];
        if (typeof opt === 'string') optionNames.push(opt);
        else if (opt && typeof opt.name === 'string') optionNames.push(opt.name);
      }
    }

    var images = [];
    var rawImages = raw.images;
    if (Object.prototype.toString.call(rawImages) === '[object Array]') {
      for (var m = 0; m < rawImages.length; m++) {
        var img = rawImages[m];
        var src = typeof img === 'string' ? img : img && img.src;
        var safe = safeImage(src);
        if (safe) images.push(safe);
      }
    }
    if (!images.length && raw.featured_image) {
      var featured = safeImage(
        typeof raw.featured_image === 'string' ? raw.featured_image : raw.featured_image.src
      );
      if (featured) images.push(featured);
    }

    var variants = [];
    var rawVariants = raw.variants;
    if (Object.prototype.toString.call(rawVariants) === '[object Array]') {
      for (var v = 0; v < rawVariants.length; v++) {
        var rv = rawVariants[v];
        if (!rv || rv.id == null) continue;
        var values = [];
        if (Object.prototype.toString.call(rv.options) === '[object Array]') {
          for (var k = 0; k < rv.options.length; k++) values.push(str(rv.options[k]));
        } else {
          /* products.json gives option1/2/3 instead of an array. */
          if (rv.option1 != null) values.push(str(rv.option1));
          if (rv.option2 != null) values.push(str(rv.option2));
          if (rv.option3 != null) values.push(str(rv.option3));
        }
        var vImage = rv.featured_image
          ? safeImage(typeof rv.featured_image === 'string' ? rv.featured_image : rv.featured_image.src)
          : '';
        variants.push({
          id: rv.id,
          title: str(rv.title) || str(rv.public_title) || '',
          options: values,
          price: centsOf(rv.price),
          compare: centsOf(rv.compare_at_price),
          available: rv.available !== false,
          image: vImage
        });
      }
    }
    if (!variants.length) return null;

    var anyAvailable = false;
    var cheapest = null;
    var compareOf = null;
    for (var a = 0; a < variants.length; a++) {
      if (variants[a].available) anyAvailable = true;
      if (variants[a].price != null && (cheapest == null || variants[a].price < cheapest)) {
        cheapest = variants[a].price;
        compareOf = variants[a].compare;
      }
    }

    return {
      handle: handle,
      title: str(raw.title) || handle,
      url: productUrl(handle),
      image: images.length ? images[0] : '',
      price: centsOf(raw.price) != null ? centsOf(raw.price) : cheapest,
      compare: centsOf(raw.compare_at_price) != null ? centsOf(raw.compare_at_price) : compareOf,
      available: typeof raw.available === 'boolean' ? raw.available : anyAvailable,
      optionNames: optionNames,
      variants: variants
    };
  }

  /* §5 — "cache num Map por pagina; nunca refaz o mesmo handle". A failure is
     cached as a resolved null, which is the documented degradation: the card
     stays a live link to the PDP instead of becoming a dead card, and we do
     not hammer a network that has already said no. */
  function loadProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    var promise = limited(function () {
      return fetchJSON(productUrl(handle) + '.js');
    }).then(
      function (data) {
        return normalizeProduct(data, handle);
      },
      function () {
        return null;
      }
    );
    productCache.set(handle, promise);
    return promise;
  }

  function loadCollection(handle) {
    return fetchJSON(collectionUrl(handle) + '/products.json?limit=' + COLLECTION_LIMIT).then(
      function (data) {
        var list = data && data.products;
        if (Object.prototype.toString.call(list) !== '[object Array]') return [];
        var out = [];
        for (var i = 0; i < list.length; i++) {
          var product = normalizeProduct(list[i], '');
          if (!product) continue;
          out.push(product);
          /* Free hydration: this endpoint already carried the variants, so
             the picker for these products needs no second request. */
          if (!productCache.has(product.handle)) {
            productCache.set(product.handle, Promise.resolve(product));
          }
        }
        return out;
      },
      function () {
        return [];
      }
    );
  }

  /* ==================================================================== */
  /* §7 mountTray — THE STRIP. The reels modal and the story dock both come
     through here, which is the whole point: there is exactly one product
     card, one picker and one add-to-cart in this codebase, so the CSS can
     never be right for one surface and orphaned on the other.             */
  /* ==================================================================== */

  function mountTray(container, payload, options) {
    if (!isEl(container) || !payload) return null;
    options = options || {};

    var compact = options.compact === true;
    var tray = el('div', 'soho-shopv__tray' + (compact ? ' soho-shopv__tray--compact' : ''));
    tray.setAttribute('role', 'group');
    tray.setAttribute('aria-label', payload.t ? 'Shop ' + payload.t : 'Shop this video');

    var state = {
      tray: tray,
      payload: payload,
      compact: compact,
      mode: options.mode === 'modal' ? 'modal' : 'tray',
      onAdded: typeof options.onAdded === 'function' ? options.onAdded : null,
      records: [],
      seen: Object.create(null),
      active: null,
      selection: [],
      rail: el('div', 'soho-shopv__rail'),
      foot: el('div', 'soho-shopv__foot'),
      dots: el('div', 'soho-shopv__dots'),
      all: null,
      picker: el('div', 'soho-shopv__picker'),
      wait: el('p', 'soho-shopv__wait', 'Loading…'),
      addBtn: null,
      errEl: null,
      busy: false,
      doneTimer: 0
    };

    state.dots.setAttribute('aria-hidden', 'true');
    state.dots.hidden = true;
    state.foot.hidden = true;
    state.picker.hidden = true;
    state.wait.hidden = true;

    tray.appendChild(state.wait);
    tray.appendChild(state.rail);
    state.foot.appendChild(state.dots);
    tray.appendChild(state.foot);
    tray.appendChild(state.picker);

    trayState.set(tray, state);

    /* §1 — tagged products first, in Dylan's order. The collection (if any)
       fills in behind them, skipping handles already on the strip. */
    addItems(state, payload.p);

    if (payload.c) {
      var label = payload.c.n
        ? 'Shop all ' + payload.c.n
        : 'Shop all' + (payload.c.t ? ' ' + payload.c.t : '');
      state.all = el('a', 'soho-shopv__all', label);
      state.all.href = payload.c.u;
      state.all.appendChild(icon(SVG_CHEVRON_RIGHT, 'soho-shopv__all__go'));
      state.foot.appendChild(state.all);
      state.foot.hidden = false;

      if (!state.records.length) state.wait.hidden = false;
      loadCollection(payload.c.h).then(function (products) {
        state.wait.hidden = true;
        if (!trayState.has(tray)) return; /* the tray was torn down mid-flight */
        var items = [];
        for (var i = 0; i < products.length; i++) {
          if (state.records.length + items.length >= COLLECTION_LIMIT) break;
          if (state.seen[products[i].handle]) continue;
          items.push({
            h: products[i].handle,
            t: products[i].title,
            u: products[i].url,
            i: products[i].image,
            pr: products[i].price,
            cp: products[i].compare,
            a: products[i].available
          });
        }
        addItems(state, items);
        if (state.all && !payload.c.n) {
          var total = state.records.length;
          state.all.firstChild.nodeValue = 'Shop all ' + total;
        }
        refreshDots(state);
        /* An empty collection with no tagged products would leave a blank
           strip; say so rather than show a void. */
        if (!state.records.length) {
          state.wait.textContent = 'Nothing to shop in this video right now.';
          state.wait.hidden = false;
        }
        /* Last, so the strip is in its final shape before the modal drops
           .soho-shopv.is-loading and re-measures against it. */
        emit(state.tray, 'soho:shopv:loaded', null);
      });
    }

    bindTray(state);
    container.appendChild(tray);
    refreshDots(state);
    return tray;
  }

  function addItems(state, items) {
    if (!items || !items.length) return;
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!item || !item.h || state.seen[item.h]) continue;
      state.seen[item.h] = true;
      var record = buildCard(state, item);
      state.records.push(record);
      state.rail.appendChild(record.el);
      hydrate(state, record);
    }
  }

  /* The card is a REAL <a href> to the PDP, always. That is what makes §5's
     degradation free: when /products/{h}.js never answers we simply do not
     preventDefault and the browser does the honest thing. It also means
     cmd-click, middle-click and "open in new tab" work like a link should. */
  function buildCard(state, item) {
    var node = el('a', 'soho-shopv__card');
    node.href = item.u;
    node.setAttribute('aria-haspopup', 'dialog');

    var img;
    if (item.i) {
      img = el('img', 'soho-shopv__card__img');
      img.src = sizedImage(item.i, 160);
      img.alt = ''; /* the title sits right next to it; announcing twice is noise */
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 64;
      img.height = 64;
    } else {
      img = el('span', 'soho-shopv__card__img');
    }
    node.appendChild(img);

    var body = el('span', 'soho-shopv__card__body');
    var title = el('span', 'soho-shopv__card__title', item.t);
    var price = el('span', 'soho-shopv__card__price');
    var was = el('s', 'soho-shopv__card__was');
    var flag = el('span', 'soho-shopv__card__flag', themeString('soldOut', 'Sold out'));
    was.hidden = true;
    flag.hidden = true;
    body.appendChild(title);
    body.appendChild(price);
    body.appendChild(was);
    body.appendChild(flag);
    node.appendChild(body);
    node.appendChild(icon(SVG_CHEVRON_RIGHT, 'soho-shopv__card__go'));

    var record = {
      item: item,
      product: null,
      status: 'idle',
      el: node,
      imgEl: img,
      titleEl: title,
      priceEl: price,
      wasEl: was,
      flagEl: flag
    };
    node.setAttribute('data-soho-shopv-handle', item.h);
    paintCard(record);
    return record;
  }

  /* Paints from whatever is known right now: Liquid's numbers on the first
     frame (§5 — no layout shift), the live product once it lands. */
  function paintCard(record) {
    var price = record.product ? record.product.price : record.item.pr;
    var compare = record.product ? record.product.compare : record.item.cp;
    var available = record.product ? record.product.available : record.item.a;

    if (record.product && record.product.title) record.titleEl.textContent = record.product.title;
    record.priceEl.textContent = price != null ? money(price) : '';
    record.priceEl.hidden = price == null;

    if (compare != null && price != null && compare > price) {
      record.wasEl.textContent = money(compare);
      record.wasEl.hidden = false;
    } else {
      record.wasEl.hidden = true;
    }

    record.flagEl.hidden = available !== false;
    if (available === false) record.el.classList.add('is-sold-out');
    else record.el.classList.remove('is-sold-out');

    /* Liquid sent no photo but the live product has one: swap the CSS
       placeholder for a real <img> instead of leaving a grey square next to a
       product that does have a picture. */
    var photo = (record.product && record.product.image) || record.item.i;
    if (photo && record.imgEl.tagName !== 'IMG') {
      var upgraded = el('img', 'soho-shopv__card__img');
      upgraded.alt = '';
      upgraded.loading = 'lazy';
      upgraded.decoding = 'async';
      upgraded.width = 64;
      upgraded.height = 64;
      upgraded.src = sizedImage(photo, 160);
      record.el.replaceChild(upgraded, record.imgEl);
      record.imgEl = upgraded;
    } else if (photo && record.imgEl.tagName === 'IMG' && !record.imgEl.getAttribute('src')) {
      record.imgEl.src = sizedImage(photo, 160);
    }
  }

  function hydrate(state, record) {
    record.status = 'loading';
    loadProduct(record.item.h).then(function (product) {
      record.product = product;
      record.status = product ? 'ready' : 'failed';
      paintCard(record);
      /* The shopper may already be staring at this picker. */
      if (state.active === record) paintPicker(state);
    });
  }

  /* ------------------------------------------------------------ the dots */

  /* Presentational on purpose: they are an indicator, not a control. Making
     them buttons would mean six 44px targets crowding a 390px strip that is
     already swipeable, scrollable and Tab-navigable through the cards. */
  function refreshDots(state) {
    var rail = state.rail;
    var pages = 1;
    if (rail.clientWidth > 0) pages = Math.ceil(rail.scrollWidth / rail.clientWidth);
    var overflows = rail.scrollWidth - rail.clientWidth > 2;

    if (!overflows || pages < 2) {
      state.dots.hidden = true;
      state.dots.textContent = '';
      if (!state.all) state.foot.hidden = true;
      return;
    }
    if (state.dots.childNodes.length !== pages) {
      state.dots.textContent = '';
      for (var i = 0; i < pages; i++) state.dots.appendChild(el('span', 'soho-shopv__dot'));
    }
    state.dots.hidden = false;
    state.foot.hidden = false;
    paintDots(state);
  }

  function paintDots(state) {
    var dots = state.dots.childNodes;
    if (!dots.length) return;
    var rail = state.rail;
    var index = 0;
    if (rail.clientWidth > 0) index = Math.round(rail.scrollLeft / rail.clientWidth);
    if (index < 0) index = 0;
    if (index > dots.length - 1) index = dots.length - 1;
    for (var i = 0; i < dots.length; i++) {
      if (i === index) dots[i].classList.add('is-active');
      else dots[i].classList.remove('is-active');
    }
  }

  /* ---------------------------------------------------------- the picker */

  function openPicker(state, record) {
    for (var i = 0; i < state.records.length; i++) state.records[i].el.classList.remove('is-active');
    record.el.classList.add('is-active');
    state.active = record;
    state.selection = [];
    paintPicker(state);
    state.picker.hidden = false;
    try {
      /* Horizontal first: bring the chosen card to the middle of the rail. */
      record.el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      /* Then vertical, and this second call is not decoration.
         MEASURED in the story dock at 391x847: the buybar is capped at 45% of
         the player (365px) and scrolls, the picker that just opened is 323px,
         and "Add to cart" landed at y=827 — below the player AND below the
         fold. It was reachable only by discovering a nested scroll, which no
         shopper does. Pulling the button's own box into view puts the chips
         and the button together inside the 365px, because 323 < 365.
         `block:'end'` and not 'nearest': 'nearest' is a no-op the moment any
         part of the element is already showing, which was exactly the case. */
      if (state.addBtn) state.addBtn.scrollIntoView({ block: 'end', inline: 'nearest' });
    } catch (e) {
      /* older engines: the rail simply stays where it is */
    }
    var target = state.picker.querySelector('.soho-shopv__opt:not(.is-unavailable)') || state.addBtn;
    if (target) {
      try {
        target.focus();
      } catch (e2) {
        /* noop */
      }
    }
    emit(state.tray, 'soho:shopv:measure', null);
  }

  function closePicker(state, returnFocus) {
    if (!state.active) return;
    var record = state.active;
    record.el.classList.remove('is-active');
    state.active = null;
    state.picker.hidden = true;
    state.picker.textContent = '';
    state.addBtn = null;
    state.errEl = null;
    state.busy = false;
    if (returnFocus !== false) {
      try {
        record.el.focus();
      } catch (e) {
        /* noop */
      }
    }
    emit(state.tray, 'soho:shopv:measure', null);
  }

  /* One full rebuild per paint. The panel is a dozen nodes; diffing it would
     be more code than it costs to throw away, and a rebuilt panel cannot
     drift out of sync with the variant that is actually selected. */
  function paintPicker(state) {
    var record = state.active;
    if (!record) return;
    var picker = state.picker;
    picker.textContent = '';
    state.addBtn = null;
    state.errEl = null;

    var product = record.product;

    /* head: thumb, title, price, and the control that puts the strip back */
    var head = el('div', 'soho-shopv__picker__head');
    if (record.item.i || (product && product.image)) {
      var thumb = el('img', 'soho-shopv__picker__img');
      thumb.src = sizedImage((product && product.image) || record.item.i, 200);
      thumb.alt = '';
      thumb.decoding = 'async';
      head.appendChild(thumb);
    }
    var info = el('div', 'soho-shopv__picker__info');
    info.appendChild(el('span', 'soho-shopv__picker__title', (product && product.title) || record.item.t));
    var priceRow = el('span', 'soho-shopv__picker__price');
    info.appendChild(priceRow);
    var wasRow = el('s', 'soho-shopv__picker__was');
    info.appendChild(wasRow);
    head.appendChild(info);

    var back = el('button', 'soho-shopv__picker__close');
    back.type = 'button';
    back.setAttribute('aria-label', 'Back to the products in this video');
    back.appendChild(icon(SVG_CLOSE, ''));
    head.appendChild(back);
    picker.appendChild(head);

    /* No variants in hand yet — still in flight, or the network said no.
       Either way the panel paints from what Liquid already gave us, so it
       never flashes empty, and the two cases differ only in the last row:
       a disabled button while we wait, a live link to the PDP when the fetch
       failed (§5 — degradation, never a dead card). */
    if (!product) {
      priceRow.textContent = record.item.pr != null ? money(record.item.pr) : '';
      wasRow.hidden = true;
      if (record.status === 'failed') {
        var pdp = el('a', 'soho-shopv__picker__pdp', 'View product page');
        pdp.href = record.item.u;
        pdp.appendChild(icon(SVG_CHEVRON_RIGHT, ''));
        picker.appendChild(pdp);
      } else {
        picker.appendChild(el('p', 'soho-shopv__wait', 'Loading sizes…'));
        var pending = el('button', 'soho-shopv__add', themeString('addToCart', 'Add to cart'));
        pending.type = 'button';
        pending.disabled = true;
        picker.appendChild(pending);
        state.addBtn = pending;
      }
      return;
    }

    /* Seed the selection from the first available variant, so the panel opens
       on something buyable instead of on a sold-out XS. */
    if (!state.selection.length) {
      var seed = null;
      for (var s = 0; s < product.variants.length; s++) {
        if (product.variants[s].available) {
          seed = product.variants[s];
          break;
        }
      }
      if (!seed) seed = product.variants[0];
      state.selection = seed.options.slice();
    }

    var showChips = product.variants.length > 1 && product.optionNames.length > 0;
    if (showChips) {
      for (var oi = 0; oi < product.optionNames.length; oi++) {
        picker.appendChild(buildOptionGroup(state, product, oi));
      }
    }

    var variant = currentVariant(state, product);
    if (variant) {
      priceRow.textContent = variant.price != null ? money(variant.price) : money(product.price);
      if (variant.compare != null && variant.price != null && variant.compare > variant.price) {
        wasRow.textContent = money(variant.compare);
        wasRow.hidden = false;
      } else {
        wasRow.hidden = true;
      }
      if (variant.image) {
        var headImg = head.querySelector('.soho-shopv__picker__img');
        if (headImg) headImg.src = sizedImage(variant.image, 200);
      }
    } else {
      priceRow.textContent = product.price != null ? money(product.price) : '';
      wasRow.hidden = true;
    }

    var err = el('p', 'soho-shopv__err');
    err.setAttribute('role', 'alert');
    err.hidden = true;
    picker.appendChild(err);
    state.errEl = err;

    var add = el('button', 'soho-shopv__add');
    add.type = 'button';
    if (!variant) {
      add.textContent = themeString('unavailable', 'Unavailable');
      add.disabled = true;
    } else if (!variant.available) {
      add.textContent = themeString('soldOut', 'Sold out');
      add.disabled = true;
    } else {
      add.textContent = themeString('addToCart', 'Add to cart');
    }
    picker.appendChild(add);
    state.addBtn = add;
  }

  function buildOptionGroup(state, product, index) {
    var group = el('div', 'soho-shopv__picker__group');
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', product.optionNames[index]);
    group.appendChild(el('span', 'soho-shopv__picker__label', product.optionNames[index]));

    var values = [];
    var seen = Object.create(null);
    for (var v = 0; v < product.variants.length; v++) {
      var value = product.variants[v].options[index];
      if (value == null || value === '' || seen[value]) continue;
      seen[value] = true;
      values.push(value);
    }

    var opts = el('div', 'soho-shopv__opts');
    for (var i = 0; i < values.length; i++) {
      var chip = el('button', 'soho-shopv__opt', values[i]);
      chip.type = 'button';
      chip.setAttribute('data-soho-shopv-opt', String(index));
      chip.setAttribute('data-soho-shopv-value', values[i]);
      var selected = state.selection[index] === values[i];
      chip.setAttribute('aria-pressed', selected ? 'true' : 'false');
      if (selected) chip.classList.add('is-selected');
      if (!valueIsAvailable(state, product, index, values[i])) {
        /* aria-disabled, not disabled: a sold-out size stays focusable and
           stays announced, which is how a screen-reader user finds out it is
           gone. The click handler refuses it. */
        chip.classList.add('is-unavailable');
        chip.setAttribute('aria-disabled', 'true');
      }
      opts.appendChild(chip);
    }
    group.appendChild(opts);
    return group;
  }

  /* Dawn's rule, deliberately: a value on option i is offered when some
     AVAILABLE variant carries it and agrees with everything already chosen to
     its left. Checking the whole selection instead would strike out every
     size the moment one colour sells out. */
  function valueIsAvailable(state, product, index, value) {
    for (var v = 0; v < product.variants.length; v++) {
      var variant = product.variants[v];
      if (!variant.available) continue;
      if (variant.options[index] !== value) continue;
      var ok = true;
      for (var j = 0; j < index; j++) {
        if (state.selection[j] != null && variant.options[j] !== state.selection[j]) {
          ok = false;
          break;
        }
      }
      if (ok) return true;
    }
    return false;
  }

  function currentVariant(state, product) {
    if (product.variants.length === 1) return product.variants[0];
    for (var v = 0; v < product.variants.length; v++) {
      var variant = product.variants[v];
      var match = true;
      for (var i = 0; i < product.optionNames.length; i++) {
        if (variant.options[i] !== state.selection[i]) {
          match = false;
          break;
        }
      }
      if (match) return variant;
    }
    return null;
  }

  function chooseOption(state, index, value) {
    var product = state.active && state.active.product;
    if (!product) return;
    state.selection[index] = value;
    /* Choosing a colour can invalidate the chosen size. Walk everything to
       the right and repair it with the first value that is still buyable. */
    for (var i = index + 1; i < product.optionNames.length; i++) {
      if (valueIsAvailable(state, product, i, state.selection[i])) continue;
      for (var v = 0; v < product.variants.length; v++) {
        var candidate = product.variants[v].options[i];
        if (candidate != null && valueIsAvailable(state, product, i, candidate)) {
          state.selection[i] = candidate;
          break;
        }
      }
    }
    paintPicker(state);
    var refocus = state.picker.querySelector(
      '.soho-shopv__opt[data-soho-shopv-opt="' + index + '"].is-selected'
    );
    if (refocus) {
      try {
        refocus.focus();
      } catch (e) {
        /* noop */
      }
    }
  }

  /* ------------------------------------------------------- add to cart */

  function cartMessage(body) {
    if (!body || typeof body !== 'object') return '';
    /* §3 / Shopify's shape: 422 answers carry `description` ("You can only
       add 3 Bahia Top to your cart"), older ones carry `message`. */
    if (typeof body.description === 'string' && body.description) return body.description;
    if (typeof body.message === 'string' && body.message) return body.message;
    return '';
  }

  function showError(state, text) {
    if (!state.errEl) return;
    state.errEl.textContent = text;
    state.errEl.hidden = false;
    emit(state.tray, 'soho:shopv:measure', null);
  }

  function hideError(state) {
    if (!state.errEl) return;
    state.errEl.textContent = '';
    state.errEl.hidden = true;
  }

  function doAdd(state) {
    var record = state.active;
    if (!record || !record.product || state.busy) return;
    var variant = currentVariant(state, record.product);
    if (!variant || !variant.available) return;

    var button = state.addBtn;
    state.busy = true;
    hideError(state);
    if (button) {
      button.classList.add('is-busy');
      button.disabled = true;
      button.textContent = 'Adding…';
    }

    fetch(cartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
    })
      .then(function (response) {
        return response.json().then(
          function (body) {
            return { ok: response.ok, body: body };
          },
          function () {
            return { ok: response.ok, body: null };
          }
        );
      })
      .then(function (result) {
        if (result.ok) {
          afterAdd(state, record, variant);
          return;
        }
        var error = new Error('cart-rejected');
        /* A raw fetch failure ("Failed to fetch", "NetworkError") never
           reaches the shopper — only a sentence Shopify actually wrote, or
           our own plain-English fallback. */
        error.sohoMessage = cartMessage(result.body);
        throw error;
      })
      .catch(function (error) {
        state.busy = false;
        if (button && button.parentNode) {
          button.classList.remove('is-busy');
          button.disabled = false;
          button.textContent = themeString('addToCart', 'Add to cart');
        }
        showError(
          state,
          (error && error.sohoMessage) || "Sorry — we couldn't add that just now. Please try again."
        );
      });
  }

  function afterAdd(state, record, variant) {
    state.busy = false;
    var detail = { handle: record.item.h, variantId: variant.id, title: record.item.t };

    /* The host is told BEFORE the theme is. The story dock's player sits at
       z-index 2147482996 and the theme's cart drawer opens at 6001, so a dock
       that is still open would hide the drawer the shopper just earned. §3
       makes the same call for the modal, by closing it first. */
    if (state.onAdded) {
      try {
        state.onAdded(detail);
      } catch (e) {
        /* a broken host callback must never eat the add */
      }
    }
    emit(state.tray, 'soho:shopv:added', detail);

    if (state.mode === 'modal') {
      closeModal();
    } else {
      var button = state.addBtn;
      if (button && button.parentNode) {
        button.classList.remove('is-busy');
        button.classList.add('is-done');
        button.textContent = 'Added';
        clearTimeout(state.doneTimer);
        state.doneTimer = setTimeout(function () {
          if (!button.parentNode) return;
          button.classList.remove('is-done');
          button.disabled = false;
          button.textContent = themeString('addToCart', 'Add to cart');
        }, DONE_MS);
      }
    }

    /* §3, verbatim. theme:cart:refresh -> cart-items.getCart();
       theme:product:added -> cart-drawer.openCartDrawerOnProductAdded(). */
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('theme:product:added', { bubbles: true }));
  }

  /* ------------------------------------------------------- tray wiring */

  function isModifiedClick(evt) {
    return !!(
      evt.metaKey ||
      evt.ctrlKey ||
      evt.shiftKey ||
      evt.altKey ||
      (typeof evt.button === 'number' && evt.button !== 0)
    );
  }

  function findRecord(state, cardEl) {
    for (var i = 0; i < state.records.length; i++) {
      if (state.records[i].el === cardEl) return state.records[i];
    }
    return null;
  }

  function bindTray(state) {
    /* (11) plain passive listener, no rAF throttle. Two comparisons. */
    state.rail.addEventListener(
      'scroll',
      function () {
        paintDots(state);
      },
      { passive: true }
    );

    state.tray.addEventListener('click', function (evt) {
      var target = evt.target;
      if (!target || !target.closest) return;

      var chip = target.closest('.soho-shopv__opt');
      if (chip && state.tray.contains(chip)) {
        evt.preventDefault();
        if (chip.getAttribute('aria-disabled') === 'true') return;
        var index = parseInt(chip.getAttribute('data-soho-shopv-opt'), 10);
        if (isNaN(index)) index = 0;
        chooseOption(state, index, chip.getAttribute('data-soho-shopv-value'));
        return;
      }

      if (target.closest('.soho-shopv__picker__close')) {
        evt.preventDefault();
        closePicker(state);
        return;
      }

      if (target.closest('.soho-shopv__add')) {
        evt.preventDefault();
        doAdd(state);
        return;
      }

      /* Real links: "Shop all" and the PDP fallback navigate, as written. */
      if (target.closest('.soho-shopv__all') || target.closest('.soho-shopv__picker__pdp')) return;

      var card = target.closest('.soho-shopv__card');
      if (!card || !state.rail.contains(card)) return;
      /* cmd/ctrl/middle-click opens the PDP in a tab, like any other link. */
      if (isModifiedClick(evt)) return;
      var record = findRecord(state, card);
      if (!record) return;
      /* §5 — the product never loaded: do not preventDefault, let the <a>
         take the shopper to the real page. Never a dead card. */
      if (record.status === 'failed') return;
      evt.preventDefault();
      if (state.active === record) closePicker(state);
      else openPicker(state, record);
    });
  }

  /* ==================================================================== */
  /* THE MODAL — one per page, built on first use, reused forever after.  */
  /* ==================================================================== */

  var modal = null;

  function buildModal() {
    var root = el('div', 'soho-shopv');
    root.hidden = true;

    var scrim = el('div', 'soho-shopv__scrim');
    scrim.setAttribute('data-soho-shopv-close', '');

    var dialog = el('div', 'soho-shopv__dialog');
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', 'Shop this video');
    dialog.tabIndex = -1;

    var close = el('button', 'soho-shopv__close');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.setAttribute('data-soho-shopv-close', '');
    close.appendChild(icon(SVG_CLOSE, ''));

    var stage = el('div', 'soho-shopv__stage');
    var title = el('h2', 'soho-shopv__title');
    title.hidden = true;

    var prev = el('button', 'soho-shopv__nav soho-shopv__nav--prev');
    prev.type = 'button';
    prev.setAttribute('aria-label', 'Previous video');
    prev.appendChild(icon(SVG_CHEVRON_LEFT, ''));
    var next = el('button', 'soho-shopv__nav soho-shopv__nav--next');
    next.type = 'button';
    next.setAttribute('aria-label', 'Next video');
    next.appendChild(icon(SVG_CHEVRON_RIGHT, ''));
    prev.hidden = true;
    next.hidden = true;

    stage.appendChild(title);
    stage.appendChild(prev);
    stage.appendChild(next);
    dialog.appendChild(close);
    dialog.appendChild(stage);
    root.appendChild(scrim);
    root.appendChild(dialog);
    document.body.appendChild(root);

    modal = {
      root: root,
      scrim: scrim,
      dialog: dialog,
      close: close,
      stage: stage,
      title: title,
      prev: prev,
      next: next,
      video: null,
      tray: null,
      payload: null,
      card: null,
      group: [],
      index: -1,
      open: false,
      lastFocus: null,
      closeTimer: 0,
      lockedByUs: false,
      attrByUs: false,
      prevOverflow: null,
      paused: [],
      observer: null,
      touch: null
    };

    root.addEventListener('click', onModalClick);
    root.addEventListener('soho:shopv:measure', measureTray);
    root.addEventListener('soho:shopv:loaded', function () {
      root.classList.remove('is-loading');
      if (modal.tray && trayState.has(modal.tray)) refreshDots(trayState.get(modal.tray));
      measureTray();
    });

    if (window.ResizeObserver) {
      modal.observer = new ResizeObserver(measureTray);
    }
    bindSwipe();
    return modal;
  }

  function onModalClick(evt) {
    var target = evt.target;
    if (!target || !target.closest) return;
    if (target.closest('[data-soho-shopv-close]')) {
      evt.preventDefault();
      closeModal();
      return;
    }
    if (target.closest('.soho-shopv__nav--prev')) {
      evt.preventDefault();
      step(-1);
      return;
    }
    if (target.closest('.soho-shopv__nav--next')) {
      evt.preventDefault();
      step(1);
      return;
    }
    /* Tap the film to pause it. No control is drawn for this on purpose: the
       video is muted and looping, so pausing is a nicety, not a function the
       shopper must be able to find. Reduced motion gets real controls
       instead (see setVideo). */
    if (modal.video && target === modal.video) {
      if (modal.video.paused) safePlay(modal.video);
      else modal.video.pause();
    }
  }

  /* The video box is remeasured rather than assumed: on a phone the strip
     floats over the film, and the film has to be centred in what is LEFT.
     The CSS carries a sane constant so this is a refinement, never a
     dependency (a stalled ResizeObserver cannot blank the layout). */
  function measureTray() {
    if (!modal || !modal.tray) return;
    var height = modal.tray.offsetHeight;
    if (!height) return;
    modal.root.style.setProperty('--shopv-tray-h', Math.round(height) + 'px');
  }

  /* ------------------------------------------------------------- media */

  /* Everything here is rooted at the card the CALLER handed us. There is no
     document-wide video query in this file (hard rule 7). */
  function mediaFromCard(card) {
    var out = { sources: [], poster: '' };
    if (!isEl(card)) return out;

    var source = card.querySelector('video');
    if (!source) {
      var tpl = card.querySelector('template[data-soho-reel-template]');
      if (tpl && tpl.content) source = tpl.content.querySelector('video');
    }
    if (source) {
      out.poster = safeImage(source.getAttribute('poster'));
      var tags = source.querySelectorAll('source');
      for (var i = 0; i < tags.length; i++) {
        var src = safeImage(tags[i].getAttribute('src'));
        if (src) out.sources.push({ src: src, type: str(tags[i].getAttribute('type')) });
      }
      if (!out.sources.length) {
        var direct = safeImage(source.getAttribute('src'));
        if (direct) out.sources.push({ src: direct, type: '' });
      }
    }
    if (!out.poster) {
      var img = card.querySelector('img');
      if (img) out.poster = safeImage(img.currentSrc || img.src);
    }
    return orderSources(out);
  }

  /* Shopify hands back an HLS playlist alongside the mp4s and only Safari
     plays it natively. A <video> takes the FIRST source it can play, so an
     m3u8 sitting first is a black box in Chrome. Push them to the back. */
  function orderSources(media) {
    var plain = [];
    var streams = [];
    for (var i = 0; i < media.sources.length; i++) {
      var entry = media.sources[i];
      if (/m3u8/i.test(entry.src) || /mpegurl/i.test(entry.type)) streams.push(entry);
      else plain.push(entry);
    }
    media.sources = plain.concat(streams);
    return media;
  }

  function normalizeMedia(opts, card) {
    var media = { sources: [], poster: safeImage(opts.poster) };
    var given = opts.video;
    if (typeof given === 'string' && safeImage(given)) {
      media.sources.push({ src: safeImage(given), type: '' });
    } else if (Object.prototype.toString.call(given) === '[object Array]') {
      for (var i = 0; i < given.length; i++) {
        var entry = given[i];
        var src = safeImage(typeof entry === 'string' ? entry : entry && entry.src);
        if (src) media.sources.push({ src: src, type: entry && entry.type ? str(entry.type) : '' });
      }
    }
    if (media.sources.length) media = orderSources(media);
    if (!media.sources.length || !media.poster) {
      var fromCard = mediaFromCard(card);
      if (!media.sources.length) media.sources = fromCard.sources;
      if (!media.poster) media.poster = fromCard.poster;
    }
    return media;
  }

  function safePlay(video) {
    if (!video || typeof video.play !== 'function') return;
    var promise;
    try {
      promise = video.play();
    } catch (e) {
      return;
    }
    /* An autoplay refusal must not throw an unhandled rejection into the
       console of a live storefront. The poster is already painted, so a
       refused play simply leaves the cover up. */
    if (promise && promise.catch) promise.catch(function () {});
  }

  function setVideo(media) {
    if (modal.video) {
      try {
        modal.video.pause();
      } catch (e) {
        /* noop */
      }
      if (modal.video.parentNode) modal.video.parentNode.removeChild(modal.video);
      modal.video = null;
    }
    if (!media.sources.length) {
      /* Stills are legitimate: a wall card can be a photo. The poster keeps
         the stage from being an empty black rectangle. */
      if (media.poster) modal.stage.style.backgroundImage = 'url("' + media.poster.replace(/"/g, '%22') + '")';
      else modal.stage.style.backgroundImage = '';
      return;
    }
    modal.stage.style.backgroundImage = '';

    var video = el('video', 'soho-shopv__video');
    video.setAttribute('playsinline', '');
    video.playsInline = true;
    video.muted = true;
    video.setAttribute('muted', '');
    video.loop = true;
    video.preload = 'metadata';
    if (media.poster) video.poster = media.poster;
    for (var i = 0; i < media.sources.length; i++) {
      var source = document.createElement('source');
      source.src = media.sources[i].src;
      if (media.sources[i].type) source.type = media.sources[i].type;
      video.appendChild(source);
    }
    /* Reduced motion: nothing plays by itself, so the shopper is given the
       browser's own controls to start it. Without them the film would be a
       still with no way in. */
    if (reducedMotion()) video.controls = true;
    modal.stage.insertBefore(video, modal.stage.firstChild);
    modal.video = video;
    if (!reducedMotion()) safePlay(video);
  }

  /* The cards behind the scrim keep playing otherwise: muted, invisible and
     burning a phone battery under a full-screen overlay. Only the videos in
     the cards the caller handed us are touched, and every one is put back the
     way it was found. */
  function pauseGroup() {
    modal.paused = [];
    for (var i = 0; i < modal.group.length; i++) {
      var video = modal.group[i].querySelector('video');
      if (video && !video.paused) {
        video.pause();
        modal.paused.push(video);
      }
    }
  }

  function resumeGroup() {
    for (var i = 0; i < modal.paused.length; i++) safePlay(modal.paused[i]);
    modal.paused = [];
  }

  /* -------------------------------------------------------- scroll lock */

  /* §4 — read the theme's signal, and write it only when nobody else has.
     `html[data-scroll-locked]` is what the theme sets for the cart drawer and
     the mobile menu; the story dock rail, the free-gift widget and the popup
     all hide themselves on it. Setting it is therefore how our dialog clears
     the persistent widgets off the screen without knowing any of their class
     names. Setting it when the theme already did would mean REMOVING the
     theme's lock on our close, so in that case we do nothing at all. */
  function lockScroll() {
    var html = document.documentElement;
    if (html.hasAttribute('data-scroll-locked')) {
      modal.lockedByUs = false;
      modal.attrByUs = false;
      return;
    }
    modal.prevOverflow = html.style.overflow;
    html.style.overflow = 'hidden';
    modal.lockedByUs = true;
    html.setAttribute('data-scroll-locked', '');
    modal.attrByUs = true;
  }

  function releaseScroll() {
    var html = document.documentElement;
    if (modal.lockedByUs) {
      /* Restore, never blank: the merchant or the theme may have had an
         inline overflow of their own before we arrived. */
      html.style.overflow = modal.prevOverflow || '';
      modal.prevOverflow = null;
      modal.lockedByUs = false;
    }
    if (modal.attrByUs) {
      html.removeAttribute('data-scroll-locked');
      modal.attrByUs = false;
    }
  }

  /* ------------------------------------------------------ keyboard / a11y */

  function trapFocus(evt) {
    var nodes = Array.prototype.filter.call(modal.dialog.querySelectorAll(FOCUSABLE), function (node) {
      if (node.hasAttribute('hidden')) return false;
      return node.offsetParent !== null || node === document.activeElement;
    });
    if (!nodes.length) {
      evt.preventDefault();
      try {
        modal.dialog.focus();
      } catch (e) {
        /* noop */
      }
      return;
    }
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (evt.shiftKey && document.activeElement === first) {
      evt.preventDefault();
      last.focus();
    } else if (!evt.shiftKey && document.activeElement === last) {
      evt.preventDefault();
      first.focus();
    } else if (!modal.dialog.contains(document.activeElement)) {
      evt.preventDefault();
      first.focus();
    }
  }

  function onKey(evt) {
    if (!modal || !modal.open) return;

    if (evt.key === 'Escape' || evt.key === 'Esc') {
      evt.preventDefault();
      /* Nested dismissal: the first Escape puts the strip back, the second
         closes the video. Anything else strands a shopper who only wanted
         out of the size chips. */
      var state = modal.tray ? trayState.get(modal.tray) : null;
      if (state && state.active) {
        closePicker(state);
        return;
      }
      closeModal();
      return;
    }

    if (evt.key === 'Tab') {
      trapFocus(evt);
      return;
    }

    if (evt.key === 'ArrowLeft' || evt.key === 'ArrowRight') {
      /* Arrows belong to whatever has focus: the strip scrolls, the chips
         move. Only the dialog itself and the nav buttons hand them to us. */
      var active = document.activeElement;
      var mine =
        active === modal.dialog ||
        (active && active.classList && active.classList.contains('soho-shopv__nav'));
      if (!mine) return;
      evt.preventDefault();
      step(evt.key === 'ArrowLeft' ? -1 : 1);
    }
  }

  function onResize() {
    if (!modal || !modal.open) return;
    measureTray();
    if (modal.tray && trayState.has(modal.tray)) refreshDots(trayState.get(modal.tray));
  }

  /* --------------------------------------------------------------- swipe */

  /* Both listeners are passive and act on touchend only: nothing here calls
     preventDefault, so the strip keeps its native horizontal scroll and the
     browser never has to wait on us to know whether it may scroll. A gesture
     that starts on the strip is the strip's. */
  function bindSwipe() {
    modal.dialog.addEventListener(
      'touchstart',
      function (evt) {
        modal.touch = null;
        if (!modal.open || !evt.touches || evt.touches.length !== 1) return;
        if (evt.target && evt.target.closest && evt.target.closest('.soho-shopv__tray')) return;
        modal.touch = { x: evt.touches[0].clientX, y: evt.touches[0].clientY, at: Date.now() };
      },
      { passive: true }
    );

    modal.dialog.addEventListener(
      'touchend',
      function (evt) {
        var start = modal.touch;
        modal.touch = null;
        if (!start || !modal.open) return;
        var touch = evt.changedTouches && evt.changedTouches[0];
        if (!touch) return;
        if (Date.now() - start.at > 800) return; /* a slow drag is not a swipe */
        var dx = touch.clientX - start.x;
        var dy = touch.clientY - start.y;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
          step(dx < 0 ? 1 : -1);
          return;
        }
        if (dy > 90 && Math.abs(dy) > Math.abs(dx)) closeModal();
      },
      { passive: true }
    );
  }

  /* ------------------------------------------------------ open / render */

  function bagOf(card) {
    if (!isEl(card)) return null;
    return card.querySelector('[data-soho-reel-bag]') || card.querySelector('.soho-reel__bag');
  }

  /* Siblings that have nothing to sell are not part of the walk: landing on
     one would show a video with an empty strip. The origin card is always in,
     because we are standing on it. */
  function buildGroup(group, card) {
    var out = [];
    var list = group && typeof group.length === 'number' ? Array.prototype.slice.call(group) : [];
    for (var i = 0; i < list.length; i++) {
      var node = list[i];
      if (!isEl(node) || out.indexOf(node) > -1) continue;
      if (node === card || readPayload(node)) out.push(node);
    }
    if (isEl(card) && out.indexOf(card) < 0) out.unshift(card);
    return out;
  }

  function renderModal(payload, card, media) {
    modal.payload = payload;
    modal.card = card;

    var titleText = payload.t || '';
    modal.title.textContent = titleText;
    modal.title.hidden = !titleText;
    modal.dialog.setAttribute(
      'aria-label',
      titleText ? titleText + ' — shop this video' : 'Shop this video'
    );

    setVideo(media || mediaFromCard(card));

    if (modal.tray) {
      if (modal.observer) modal.observer.disconnect();
      trayState.delete(modal.tray);
      if (modal.tray.parentNode) modal.tray.parentNode.removeChild(modal.tray);
      modal.tray = null;
    }

    /* §2 — .is-loading is written exactly here: the one moment the strip is
       genuinely empty, i.e. a collection-only payload whose products have not
       landed yet. It is removed on soho:shopv:loaded (see buildModal). */
    modal.root.classList.remove('is-loading');
    if (payload.c && !payload.p.length) modal.root.classList.add('is-loading');

    modal.tray = mountTray(modal.dialog, payload, { mode: 'modal' });

    var many = modal.group.length > 1;
    modal.prev.hidden = !many;
    modal.next.hidden = !many;
    modal.prev.disabled = !many || modal.index <= 0;
    modal.next.disabled = !many || modal.index >= modal.group.length - 1;

    if (modal.observer && modal.tray) modal.observer.observe(modal.tray);
    measureTray();
    if (modal.tray && trayState.has(modal.tray)) refreshDots(trayState.get(modal.tray));
  }

  function step(dir) {
    if (!modal || !modal.open || modal.group.length < 2) return;
    var next = modal.index;
    for (var guard = 0; guard < modal.group.length; guard++) {
      next += dir;
      /* No wrap-around. The ends are the ends, and the arrows are disabled
         there, so the shopper is never sent back to where they started
         wondering whether the button worked. */
      if (next < 0 || next >= modal.group.length) return;
      var card = modal.group[next];
      var payload = readPayload(card);
      if (!payload) continue;
      modal.index = next;
      renderModal(payload, card, mediaFromCard(card));
      pauseGroup();
      return;
    }
  }

  function openModal(opts) {
    opts = opts || {};
    var card = isEl(opts.card) ? opts.card : null;
    var payload = normalizePayload(opts.payload) || (card ? readPayload(card) : null);
    /* §1 rule 4 — nothing to sell, no modal. The caller's <a href> is left to
       do its job and the shopper lands on the PDP. */
    if (!payload) return false;

    if (!modal) buildModal();
    clearTimeout(modal.closeTimer);

    modal.group = buildGroup(opts.group, card);
    modal.index = card ? modal.group.indexOf(card) : -1;

    if (!modal.open) {
      modal.lastFocus = (isEl(opts.trigger) && opts.trigger) || bagOf(card) || document.activeElement;
      /* §7 — the caller may hand us a close callback instead of listening for
         `soho:shopv:close`. The reels section passes one and its own comments
         treat it as a live path; an argument that is accepted and then ignored
         is worse than one that does not exist, because the next reader trusts
         it. Kept per-open, cleared on teardown. */
      modal.onClose = typeof opts.onClose === 'function' ? opts.onClose : null;
      lockScroll();
      modal.root.hidden = false;
      /* (11) The open class is added SYNCHRONOUSLY, after a forced reflow —
         never in requestAnimationFrame, which does not run in a backgrounded
         tab and would leave a transparent full-screen overlay swallowing
         every click on the page. The reflow is what gives the transition a
         from-state to animate out of. */
      void modal.root.offsetWidth;
      modal.root.classList.remove('is-closing');
      modal.root.classList.add('is-open');
      modal.open = true;
      /* (12) The two SOHO floaters live ABOVE us on purpose: `.ss-float` at
         2147482999 and the free-gift bar `.soho-fg` at 2147483000, against our
         2147482996. That ordering is the house rule and we are not going to
         win a z-index race against it — measured on the live store, the bar is
         permanently on screen and lands exactly where the product strip does.
         So we do what every lightbox does: while we are open, they stand down.
         The class is the only lever; the CSS beside it does the hiding. */
      document.documentElement.classList.add('soho-shopv-open');
      /* §9.1 — the reels section pauses its own card off this event instead of
         polling for our `.is-open` class. */
      emit(document, 'soho:shopv:open', { payload: payload });
      document.addEventListener('keydown', onKey, true);
      window.addEventListener('resize', onResize);
    }

    renderModal(payload, card, normalizeMedia(opts, card));
    pauseGroup();

    try {
      modal.dialog.focus();
    } catch (e) {
      /* noop */
    }
    return true;
  }

  function closeModal() {
    if (!modal || !modal.open) return;
    modal.open = false;
    clearTimeout(modal.closeTimer);

    if (modal.video) {
      try {
        modal.video.pause();
      } catch (e) {
        /* noop */
      }
    }

    modal.root.classList.remove('is-open');
    modal.root.classList.add('is-closing');
    /* Give the floaters back the moment we start leaving — they fade on the
       same curve as the scrim, so the bar does not pop in on an empty screen.
       This runs on EVERY exit (X, scrim, ESC, swipe, and the close that
       precedes an add-to-cart), because closeModal is the single door out. */
    document.documentElement.classList.remove('soho-shopv-open');
    emit(document, 'soho:shopv:close', {});
    if (modal.onClose) {
      var cb = modal.onClose;
      modal.onClose = null;
      /* A caller that throws must not leave the modal half-closed: the scroll
         lock and the focus return are still ahead of us in this function. */
      try {
        cb();
      } catch (e0) {
        /* noop */
      }
    }
    releaseScroll();
    document.removeEventListener('keydown', onKey, true);
    window.removeEventListener('resize', onResize);
    resumeGroup();

    /* Focus goes home before anything else can take it: after an add, the
       theme's cart drawer opens a beat later and moves focus itself, which is
       the right final destination. */
    var home = bagOf(modal.card) || modal.lastFocus;
    modal.lastFocus = null;
    if (home && document.contains(home)) {
      try {
        home.focus();
      } catch (e2) {
        /* noop */
      }
    }

    modal.closeTimer = setTimeout(teardown, reducedMotion() ? 0 : CLOSE_MS);
  }

  function teardown() {
    if (!modal || modal.open) return; /* reopened during the fade */
    modal.root.hidden = true;
    modal.root.classList.remove('is-closing');
    modal.root.classList.remove('is-loading');
    if (modal.observer) modal.observer.disconnect();
    if (modal.tray) {
      trayState.delete(modal.tray);
      if (modal.tray.parentNode) modal.tray.parentNode.removeChild(modal.tray);
      modal.tray = null;
    }
    /* Destroys our <video>, which is what actually releases the download. */
    setVideo({ sources: [], poster: '' });
    modal.stage.style.backgroundImage = '';
    modal.card = null;
    modal.onClose = null;
    modal.payload = null;
    modal.group = [];
    modal.index = -1;
  }

  /* ==================================================================== */
  /* §7 — the public API. One object, four methods, nothing else global.  */
  /* ==================================================================== */

  window.SohoShopVideo = {
    /** Opens the modal. Returns true if it opened, false when there was
     *  nothing to sell (§1 rule 4) — the caller must then let its own
     *  <a href> navigate to the PDP.
     *  opts: { payload, card, group, video, poster } */
    open: function (opts) {
      try {
        return openModal(opts);
      } catch (e) {
        /* A throw here would swallow the click AND leave no navigation. */
        return false;
      }
    },

    close: function () {
      try {
        closeModal();
      } catch (e) {
        /* noop */
      }
    },

    /** Builds the strip (and its picker) inside a container that already
     *  exists. This is what the story dock calls: the dock has its own
     *  player and never opens this modal. Returns the .soho-shopv__tray.
     *  options: { compact: true, onAdded: fn } — onAdded fires BEFORE the
     *  theme's cart events so the host can close its own player first; the
     *  same information also arrives as a bubbling `soho:shopv:added`. */
    mountTray: function (container, payload, options) {
      try {
        var clean = normalizePayload(payload);
        if (!clean) return null;
        return mountTray(container, clean, options);
      } catch (e) {
        return null;
      }
    },

    /** Reads and validates a card's <script data-soho-shop>. Returns null if
     *  it is missing, malformed, or has nothing sellable. Every handle check
     *  in the storefront lives here, in one place (§1 rule 2). */
    readPayload: readPayload
  };
})();
